import { action, runInAction, observable, toJS } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import partnerController from '../controller';
import {
  PartnerOffice,
  GetPartnerOfficeRequest,
  ServiceOfficeInterface,
  CreateServiceOfficeInterface,
  UpdateServiceOfficeInterface,
  ServiceOfficeRefValueResponse,
  CreateServiceOfficeRefValueRequestInterface,
  RefValuesForOfficeRequestInterface,
  RefValuesForOfficeItem,
  RunflatForOfficeRequestInterface,
  RunflatForOfficeItem,
  RefValuesForOfficeCacheInterface,
  RunflatForOfficeCacheInterface,
  OfficeCorporatePartnerItemInterface,
  CreateOfficeCorporatePartnerInterface,
  UpdateOfficeCorporatePartnerInterface,
  OfficeCorporatePartnerRequestInterface,
  UserByPartnerOfficeItem,
  CreateUserByPartnerOfficeInterface,
  PartnerOfficeDomainItemInterface,
  CreatePartnerOfficeDomainInterface,
  // DomainsListInterface,
} from './officesApi';

export interface PartnerOfficeStore {
  officesForSelector: PartnerOffice[],
  officesForSelectorByRequest: GetPartnerOfficeRequest,
  officeServicesForSelector: ServiceOfficeInterface[], // Данные привязки сервис-офис
  officeServicesForSelectorByOfficeId: string, // partnerOfficeId, по которому лежат данные в officeServicesForSelector

  // блок  значений для списка сервис-офисов в карточке офиса:
  officeServices: ServiceOfficeInterface[], // сервисы по офису
  officeServicesByOfficeId: string, // officeServiceId, по которому лежат данные в officeService
  currentOfficeService: ServiceOfficeInterface,

  // блок  значений для сервис-офисов-refValues в карточке Service-Office:
  serviceOfficeRefValues: ServiceOfficeRefValueResponse, // услуги по serviceOffice
  serviceOfficeRefValuesByServiceOfficeId: string, // serviceOfficeId , по которому лежат данные в serviceOfficeRefValues

  // блок значений для услуг(УС), привязанные к сервис-офису (используется для селекторов)
  refValuesForOffice15: RefValuesForOfficeItem[];
  refValuesForOfficeRequest15: RefValuesForOfficeRequestInterface;
  refValuesForOffice56: RefValuesForOfficeItem[];
  refValuesForOfficeRequest56: RefValuesForOfficeRequestInterface;
  refValuesForOffice10: RefValuesForOfficeItem[];
  refValuesForOfficeRequest10: RefValuesForOfficeRequestInterface;
  // runflatForOfficeRequest: RunflatForOfficeRequestInterface,
  runflatForOffice: RunflatForOfficeItem,

  refValuesForOfficeCache: RefValuesForOfficeCacheInterface[],
  runflatForOfficeCache: RunflatForOfficeCacheInterface[],

    // блок  значений для списка "Привязки корп. партнеров к офисам"
    // Т.е. указываем, каких корпоративных клиентов принимают в указанном офисе
  officeCorporatePartners: OfficeCorporatePartnerItemInterface[],
  officeCorporatePartnersByOfficeId: string,
  currentOfficeCorporatePartner: OfficeCorporatePartnerItemInterface,

  // блок  значений связки Офис-Пользователь
  userByPartnerOffice: UserByPartnerOfficeItem[],
  currentUserByPartnerOffice: UserByPartnerOfficeItem,
  userByPartnerOfficeId: string,

  partnerOfficeDomains: PartnerOfficeDomainItemInterface[],
  partnerOfficeDomainsById: string,
  domainsList: string[],

  // блок бывш. Redux-слайса partnerOffices (список офисов + привязки сервис-офис для чекбокс-панелей)
  /** офисы последнего запроса (бывш. partnerOffices.list): список по партнёру (страница
   *  «Офисы», модалки тарифов/сервисов) или по сервису/сервису+партнёру (селектор в заявке) */
  officesList: PartnerOffice[],
  /** partnerId последнего запроса офисов по партнёру (бывш. listByPartnerId) —
   *  гейт «партнёр сменился — перечитать» в потребителях */
  officesListPartnerId: string | null,
  loadingOfficesList: boolean,
  /** офисы по связке сервис+партнёр (бывш. officeByPartnerAndService) — отдельный слот
   *  от officesList: оба читаются одновременно в модалке сервисов партнёра */
  officeByPartnerAndService: PartnerOffice[],
  /** привязки сервис-офис офиса, полный список с признаком available (бывш.
   *  partnerOffices.officeServices) — чекбокс-панели; НЕ фильтруется, в отличие от
   *  officeServices выше (селекторы карточки офиса) */
  officeServiceBindings: ServiceOfficeInterface[],
  officeServiceBindingsByOfficeId: string | null,

  error: ErrorInterface,
}

const initialValues: PartnerOfficeStore = {
  officesForSelector: [],
  officesForSelectorByRequest: null,
  officeServicesForSelector: [], // сервисы по офису
  officeServicesForSelectorByOfficeId: null, // officeServiceId, по которому лежат данные в officeService

  officeServices: [],
  officeServicesByOfficeId: null,
  currentOfficeService: null,

  serviceOfficeRefValues: { items: [] },
  serviceOfficeRefValuesByServiceOfficeId: null,

  // блок значений для услуг(УС), привязанные к сервис-офису (используется для селекторов)
  refValuesForOffice15: [],
  refValuesForOfficeRequest15: null,
  refValuesForOffice56: [],
  refValuesForOfficeRequest56: null,
  refValuesForOffice10: [],
  refValuesForOfficeRequest10: null,
  // runflatForOfficeRequest: null,
  runflatForOffice: null,

  refValuesForOfficeCache: [],
  runflatForOfficeCache: [],

  officeCorporatePartners: [],
  officeCorporatePartnersByOfficeId: null,
  currentOfficeCorporatePartner: null,

  userByPartnerOffice: [],
  currentUserByPartnerOffice: null,
  userByPartnerOfficeId: null,

  partnerOfficeDomains: [],
  partnerOfficeDomainsById: null,
  domainsList: [],

  officesList: [],
  officesListPartnerId: null,
  loadingOfficesList: false,
  officeByPartnerAndService: [],
  officeServiceBindings: [],
  officeServiceBindingsByOfficeId: null,

  error: null,
};

export const partnerOfficeStore = observable(initialValues);

export const partnerOfficeActions = {

  async getOfficesForSelector(request :GetPartnerOfficeRequest) {
    partnerOfficeActions.clearError();
    // reconciliationActions.setLoading();
    runInAction(() => {
      partnerOfficeStore.officesForSelectorByRequest = { ...request };
    });
    const response = await partnerController.getPartnerOfficeList(request);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        // пока шёл запрос, мог прийти более новый (пользователь сменил сервис/партнёра):
        // устаревший ответ отбрасываем, иначе в селекторе останутся офисы прошлого запроса
        if (isEqual(toJS(partnerOfficeStore.officesForSelectorByRequest), { ...request })) {
          partnerOfficeStore.officesForSelector = response.data?.items ?? [];
        }
      }
    //  schedulerReferenceStore.loading = false;
    });
  },

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      partnerOfficeStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      // schedulerReferenceStore.loading = false;
      throw errorData;
    }
  }),

  clearOfficesForSelector: action(() => {
    partnerOfficeStore.officesForSelectorByRequest = null;
    partnerOfficeStore.officesForSelector = [];
  }),

  async clearError() {
    partnerOfficeStore.error = null;
  },

  async getPartnerOfficeServicesForSelector(partnerOfficeId) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.officeServicesForSelectorByOfficeId = partnerOfficeId;
    });
    const response = await partnerController.getPartnerOfficeServices(partnerOfficeId);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.officeServicesForSelector = response.data.filter((v) => v?.available);
      }
    });
  },

  clearOfficesServicesForSelector: action(() => {
    partnerOfficeStore.officeServicesForSelectorByOfficeId = null;
    partnerOfficeStore.officeServicesForSelector = [];
  }),

  async getPartnerOfficeServices(partnerOfficeId) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.officeServicesForSelectorByOfficeId = partnerOfficeId;
    });
    const response = await partnerController.getPartnerOfficeServices(partnerOfficeId);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.officeServices = response.data.filter((v) => v?.available);
      }
    });
  },

  createPartnerOfficeService: async (request: CreateServiceOfficeInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.createPartnerOfficeService(request);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      const x = cloneDeep(partnerOfficeStore.officeServices);
      x.push(response.data);
      runInAction(() => {
        partnerOfficeStore.officeServices = cloneDeep(x);
      });
    }
  },

  updatePartnerOfficeService: async (id: string, request: UpdateServiceOfficeInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.updatePartnerOfficeService({ ...request, id });
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.officeServices = partnerOfficeStore.officeServices
          .map((v) => (v.id === id
            ? {
              ...v,
              ...response.data,
            }
            : { ...v }));
      }
    });
  },

  deletePartnerOfficeService: async (id: string): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.deletePartnerOfficeService(id);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.officeServices = partnerOfficeStore.officeServices.filter((v) => v.id !== id);
      }
    });
  },

  clearOfficesServices: action(() => {
    partnerOfficeStore.officeServicesByOfficeId = null;
    partnerOfficeStore.officeServices = [];
  }),

  setCurrentOfficeService: action((serviceOffice: ServiceOfficeInterface) => {

    partnerOfficeStore.currentOfficeService = serviceOffice;
  }),

  clearCurrentOfficeService: action(() => {
    partnerOfficeStore.currentOfficeService = null;
  }),

  // блок  значений для сервис-офисов-refValues в карточке Service-Office:
  async getServiceOfficeRefValues(serviceOfficeId) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.serviceOfficeRefValuesByServiceOfficeId = serviceOfficeId;
    });
    const response = await partnerController.getServiceOfficeRefValues(serviceOfficeId);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.serviceOfficeRefValues = response.data;
      } else partnerOfficeActions.clearServiceOfficeRefValues();
    });
  },

  createServiceOfficeRefValues: async (request: CreateServiceOfficeRefValueRequestInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.createServiceOfficeRefValues(request);
    partnerOfficeActions.handleError(response);
  },

  deleteServiceOfficeRefValues: async (id: string): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.deleteServiceOfficeRefValues(id);
    partnerOfficeActions.handleError(response);
  },

  clearServiceOfficeRefValues: action(() => {
    partnerOfficeStore.serviceOfficeRefValuesByServiceOfficeId = null;
    partnerOfficeStore.serviceOfficeRefValues = { items: [] };
  }),

  // блок  значений для сервис-офисов-refValues ( в карточке Service-Office задаются)
  // и используются в селекторах в журнале записи и заявках(?)
  // P.S. Данные кешируются, чтоб снизить  количество запросов к бэку
  async getRefValuesForOffice(request: RefValuesForOfficeRequestInterface) {
    partnerOfficeActions.clearError();
    const cachedValue = partnerOfficeActions.getRefValuesForOfficeCache(request);

    if (cachedValue) {
      runInAction(() => {
        partnerOfficeActions.setRefValuesForOfficeRequest(request.refId, request);
        partnerOfficeActions.setRefValuesForOffice(request.refId, cachedValue.refValuesForOfficeResponse);
      });
    } else {
      await runInAction(() => {
        partnerOfficeActions.setRefValuesForOfficeRequest(request.refId, request);
      });
      const response = await partnerController.getRefValuesForOffice(request);
      partnerOfficeActions.handleError(response);
      runInAction(() => {
        if (!response.errorData) {
          partnerOfficeActions.setRefValuesForOffice(request.refId, response.data?.items);
          partnerOfficeActions.addRefValuesForOfficeCache(
            { refValuesForOfficeRequest: request, refValuesForOfficeResponse: response.data?.items }
          );
        } else partnerOfficeActions.clearRefValuesForOffice(request.refId);
      });
    }
  },

  setRefValuesForOfficeRequest: (refId: number, request: RefValuesForOfficeRequestInterface) => {

    switch (refId) {
      case 15: partnerOfficeStore.refValuesForOfficeRequest15 = request;
        break;
      case 10: partnerOfficeStore.refValuesForOfficeRequest10 = request;
        break;
      case 56: partnerOfficeStore.refValuesForOfficeRequest56 = request;
        break;
    }
  },

  setRefValuesForOffice: (refId: number, data: RefValuesForOfficeItem[]) => {

    switch (refId) {
      case 15: partnerOfficeStore.refValuesForOffice15 = data;
        break;
      case 10: partnerOfficeStore.refValuesForOffice10 = data;
        break;
      case 56: partnerOfficeStore.refValuesForOffice56 = data;
        break;
    }
  },

  clearRefValuesForOffice: action((refId: number) => {

    switch (refId) {
      case 15:
        partnerOfficeStore.refValuesForOfficeRequest15 = null;
        partnerOfficeStore.refValuesForOffice15 = [];
        break;
      case 10:
        partnerOfficeStore.refValuesForOfficeRequest10 = null;
        partnerOfficeStore.refValuesForOffice10 = [];
        break;
      case 56:
        partnerOfficeStore.refValuesForOfficeRequest56 = null;
        partnerOfficeStore.refValuesForOffice56 = [];
        break;
    }
  }),

  addRefValuesForOfficeCache: (data: RefValuesForOfficeCacheInterface) => {
    partnerOfficeStore.refValuesForOfficeCache.push(toJS(data));
  },

  getRefValuesForOfficeCache: (request: RefValuesForOfficeRequestInterface):RefValuesForOfficeCacheInterface => {
    return partnerOfficeStore.refValuesForOfficeCache.find((v) => isEqual(v.refValuesForOfficeRequest, request));
  },

  getPartnerOfficeServiceByOfficeAndService: async (request :RunflatForOfficeRequestInterface) => {
    const cachedValue = partnerOfficeActions.getRunflatForOfficeCache(request);
    if (cachedValue) {
      return cachedValue.runflatForOfficeResponse;
    }
    const response = await partnerController.getPartnerOfficeServiceByOfficeAndService(request);
    partnerOfficeActions.addRunflatForOfficeCache({
      runflatForOfficeRequest: request,
      runflatForOfficeResponse: response,
    });
    return response;
  },

  addRunflatForOfficeCache: (data: RunflatForOfficeCacheInterface) => {
    partnerOfficeStore.runflatForOfficeCache.push(toJS(data));
  },

  getRunflatForOfficeCache: (request: RunflatForOfficeRequestInterface):RunflatForOfficeCacheInterface => {
    return partnerOfficeStore.runflatForOfficeCache.find((v) => isEqual(v.runflatForOfficeRequest, request));
  },

  clearPartnerOfficeServiceByOfficeAndService: action(() => {
    partnerOfficeStore.runflatForOffice = null;
    // partnerOfficeStore.runflatForOfficeRequest = null;
  }),
  // конец блока  значений для сервис-офисов-refValue

  // блок  значений для корпоративных партнёров офисов:
  async getOfficeCorporatePartners(request:OfficeCorporatePartnerRequestInterface) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.officeCorporatePartnersByOfficeId = request.officeId;
    });
    const response = await partnerController.getOfficeCorporatePartners(request);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.officeCorporatePartners = response.data.items;
      } else partnerOfficeActions.clearOfficeCorporatePartners();
    });
  },

  setCurrentOfficeCorporatePartner: async (request: OfficeCorporatePartnerItemInterface | null): Promise<void> => {
    partnerOfficeActions.clearError();
    partnerOfficeStore.currentOfficeCorporatePartner = request;
  },

  createOfficeCorporatePartner: async (request: CreateOfficeCorporatePartnerInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.createOfficeCorporatePartner(request);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      partnerOfficeStore.officeCorporatePartners.push(response.data);
    }
  },

  updateOfficeCorporatePartner: async (request: UpdateOfficeCorporatePartnerInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.updateOfficeCorporatePartner(request);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      const ind: number = partnerOfficeStore.officeCorporatePartners.findIndex((v) => v.id === request.id);
      partnerOfficeStore.officeCorporatePartners[ind] = {
        ...partnerOfficeStore.officeCorporatePartners[ind],
        ...request,
      };
    }
  },

  deleteOfficeCorporatePartner: async (id: string): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.deleteOfficeCorporatePartner(id);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      partnerOfficeStore.officeCorporatePartners.find((v) => v.id === id).deletedDate = 'deleted';
    }
  },

  restoreOfficeCorporatePartner: async (id: string): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.restoreOfficeCorporatePartner(id);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      partnerOfficeStore.officeCorporatePartners.find((v) => v.id === id).deletedDate = null;
    }
  },

  clearOfficeCorporatePartners: action(() => {
    partnerOfficeStore.officeCorporatePartnersByOfficeId = null;
    partnerOfficeStore.officeCorporatePartners = [];
  }),
  // конец блок  значений для корпоративных партнёров офисов:

  // блок  значений для связки пользователь-офис:

  async getUserByOffice(officeId:string) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.userByPartnerOfficeId = officeId;
    });
    const response = await partnerController.getUserByOffice(officeId);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.userByPartnerOffice = response.data.items;
      } else partnerOfficeActions.clearUserByOffice();
    });
  },

  createUserByOffice: async (request: CreateUserByPartnerOfficeInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.createUserByOffice(request);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      // partnerOfficeStore.officeCorporatePartners.push(response.data);
    }
  },

  deleteUserByOffice: async (id: number): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.deleteUserByOffice(id);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      // partnerOfficeStore.officeCorporatePartners.find(v => v.id === id).deletedDate = 'deleted';
    }
  },

  setCurrentUserByPartnerOffice: async (request: UserByPartnerOfficeItem | null): Promise<void> => {
    partnerOfficeActions.clearError();
    partnerOfficeStore.currentUserByPartnerOffice = request;
  },

  clearUserByOffice: action(() => {
    partnerOfficeStore.userByPartnerOfficeId = null;
    partnerOfficeStore.currentUserByPartnerOffice = null;
    partnerOfficeStore.userByPartnerOffice = [];
  }),
  // конец блок  значений для связки пользователь офис

  // блок  значений для доменов офиса:
  async getPartnerOfficeDomains(officeId:string) {
    partnerOfficeActions.clearError();
    await action(() => {
      partnerOfficeStore.partnerOfficeDomainsById = officeId;
    });
    const response = await partnerController.getPartnerOfficeDomains(officeId);
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.partnerOfficeDomains = response.data.items;
      } else partnerOfficeActions.clearPartnerOfficeDomains();
    });
  },

  createPartnerOfficeDomain: async (request: CreatePartnerOfficeDomainInterface): Promise<void> => {
    partnerOfficeActions.clearError();
    const response = await partnerController.createPartnerOfficeDomain(request);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      partnerOfficeStore.partnerOfficeDomains.push(response.data);
    }
  },

  deletePartnerOfficeDomain: async (id: string): Promise<void> => {
    if (partnerOfficeStore.error) { partnerOfficeActions.clearError(); }
    const response = await partnerController.deletePartnerOfficeDomain(id);
    partnerOfficeActions.handleError(response);
    if (!response.errorData) {
      partnerOfficeStore.partnerOfficeDomains = partnerOfficeStore.partnerOfficeDomains.filter((v) => v.id !== id);
    }
  },

  clearPartnerOfficeDomains: action(() => {
    partnerOfficeStore.partnerOfficeDomainsById = null;
    partnerOfficeStore.partnerOfficeDomains = [];
  }),

  async getUniqueDomains() {
    partnerOfficeActions.clearError();
    const response = await partnerController.getUniqueDomains();
    partnerOfficeActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        partnerOfficeStore.domainsList = response.data.items;
      };
    });
  },

  addNewDomain: async (domain: string): Promise<void> => {
    partnerOfficeActions.clearError();
    if (partnerOfficeStore.domainsList.indexOf(domain) < 0 )
    { partnerOfficeStore.domainsList.push(domain); }
  },

  // конец блок значений для доменов офиса:

  // блок бывш. Redux-слайса partnerOffices (удалён 2026-09-23):
  // GET /v1/partner_office?partnerId= — офисы партнёра (страница «Офисы» на карточке
  // партнёра, модалки тарифов/сервисов). Слот без кэша, 1:1 с Redux: страница
  // перечитывает список при каждом loadData (mount/смена партнёра/ showDeleted/после CRUD).
  async getPartnerOfficeList(params: { partnerId?: string, showDeleted?: boolean } & Record<string, unknown>) {
    runInAction(() => {
      partnerOfficeStore.officesListPartnerId = (params?.partnerId as string) ?? null;
      partnerOfficeStore.loadingOfficesList = true;
    });
    try {
      const response = await partnerController.getPartnerOfficeList(params);

      partnerOfficeActions.handleError(response);
      runInAction(() => {
        partnerOfficeStore.officesList = response?.data?.items ?? [];
      });
    } catch (err) {
      partnerOfficeActions.handleError(err);
      runInAction(() => {
        partnerOfficeStore.officesList = [];
        partnerOfficeStore.officesListPartnerId = null;
      });
    } finally {
      runInAction(() => {
        partnerOfficeStore.loadingOfficesList = false;
      });
    }
  },

  // GET /v1/service_office/offices?serviceId= — офисы сервиса (селектор «Офис» в заявке,
  // хранение): пишут в тот же слот officesList, что и список по партнёру (как в Redux).
  async getPartnerOfficesForService(serviceId: string | number, showDeleted = false) {
    try {
      const response = await partnerController.getPartnerOfficesForService(serviceId, showDeleted);

      partnerOfficeActions.handleError(response);
      runInAction(() => {
        partnerOfficeStore.officesList = response?.data ?? [];
      });
    } catch (err) {
      partnerOfficeActions.handleError(err);
    }
  },

  async getPartnerOfficesForServiceAndPartner(serviceId: string | number, partnerId: string, showDeleted = false) {
    try {
      const response = await partnerController.getPartnerOfficesForServiceAndPartner(serviceId, partnerId, showDeleted);

      partnerOfficeActions.handleError(response);
      runInAction(() => {
        partnerOfficeStore.officesList = response?.data ?? [];
      });
    } catch (err) {
      partnerOfficeActions.handleError(err);
    }
  },

  clearPartnerOffice: action(() => {
    partnerOfficeStore.officesList = [];
    partnerOfficeStore.officesListPartnerId = null;
    partnerOfficeStore.loadingOfficesList = false;
    partnerOfficeStore.error = null;
  }),

  // CRUD офисов (POST/PUT/DELETE/restore POST /v1/partner_office) — модалка редактора
  // офиса. Кэш officesList не трогаем: страница офисов перечитывает список после CRUD.
  // При ошибке — error в стор + исключение (модалка показывает toastr и пишет в форму).
  // 1:1 с бывш. Redux-экшеном: созданный офис сразу деактивируется (задача 5212 — новый
  // офис не должен сразу появляться в клиентском приложении).
  async createPartnerOffice(params: Record<string, unknown>): Promise<void> {
    const response = await partnerController.createPartnerOffice(params);
    partnerOfficeActions.handleError(response);
    if (response.data?.id) { await partnerController.deletePartnerOffice(response.data?.id); }
  },

  async updatePartnerOffice(params: Record<string, unknown> & { id: string }): Promise<void> {
    const response = await partnerController.updatePartnerOffice(params);
    partnerOfficeActions.handleError(response);
  },

  async deletePartnerOffice(partnerOfficeId: string): Promise<void> {
    const response = await partnerController.deletePartnerOffice(partnerOfficeId);
    partnerOfficeActions.handleError(response);
  },

  async restorePartnerOffice(partnerOfficeId: string): Promise<void> {
    const response = await partnerController.restorePartnerOffice(partnerOfficeId);
    partnerOfficeActions.handleError(response);
  },

  // GET /v1/service_office/offices?serviceId=&partnerId= — офисы связки сервис+партнёр
  // в отдельный слот (бывш. getOfficesByServiceAndPartner дублем контроллерного метода).
  // clear-экшен чинит бывш. Redux-баг: тот писал в несуществующие ключи и ничего не чистил.
  async getOfficesByServiceAndPartner(params: {
    serviceId: string | number, partnerId: string, showDeleted?: boolean,
  }) {
    try {
      const response = await partnerController.getPartnerOfficesForServiceAndPartner(
        params.serviceId, params.partnerId, params?.showDeleted
      );

      partnerOfficeActions.handleError(response);
      runInAction(() => {
        partnerOfficeStore.officeByPartnerAndService = response?.data ?? [];
      });
    } catch (err) {
      partnerOfficeActions.handleError(err);
    }
  },

  clearOfficesByServiceAndPartner: action(() => {
    partnerOfficeStore.officeByPartnerAndService = [];
    partnerOfficeStore.error = null;
  }),

  // GET /v1/service_office/services?partnerOfficeId= — привязки сервис-офис офиса,
  // ПОЛНЫЙ список с available (чекбокс-панели в модалках сервисов/тарифов);
  // officeServices выше — фильтрованная выборка для селекторов карточки офиса.
  async getServiceOfficeBindings(partnerOfficeId: string) {
    runInAction(() => {
      partnerOfficeStore.officeServiceBindingsByOfficeId = partnerOfficeId;
    });
    try {
      const response = await partnerController.getPartnerOfficeServices(partnerOfficeId);

      partnerOfficeActions.handleError(response);
      runInAction(() => {
        partnerOfficeStore.officeServiceBindings = response?.data ?? [];
      });
    } catch (err) {
      partnerOfficeActions.handleError(err);
      runInAction(() => {
        partnerOfficeStore.officeServiceBindings = [];
        partnerOfficeStore.officeServiceBindingsByOfficeId = null;
      });
    }
  },

  clearServiceOfficeBindings: action(() => {
    partnerOfficeStore.officeServiceBindings = [];
    partnerOfficeStore.officeServiceBindingsByOfficeId = null;
    partnerOfficeStore.error = null;
  }),

  // привязка/отвязка сервис-офис (POST/DELETE /v1/service_office) для чекбокс-панелей.
  // 1:1 с бывш. Redux-экшеном createPartnerOfficeService: дубль привязки
  // (BINDING_ALREADY_EXISTS) — не ошибка, молча игнорируем.
  async createServiceOfficeBinding(params: {
    serviceId: string | number, partnerOfficeId: string,
  }): Promise<void> {
    const response = await partnerController.createPartnerOfficeService(params);
    if (response.errorData && response.errorData.errorCode !== 'BINDING_ALREADY_EXISTS') {
      partnerOfficeActions.handleError(response);
    }
  },

  async deleteServiceOfficeBinding(id: string): Promise<void> {
    const response = await partnerController.deletePartnerOfficeService(id);
    partnerOfficeActions.handleError(response);
  },

};
